// Relays run by teams (docs/school-carnivals-step-3-relays.md in the portal
// repo, 2.1 to 2.3). In a school a relay is run by its houses, and a team with
// a typed name is an ad hoc team ("Staff"). In a club a relay is run by teams
// with typed names. No athlete owns a team's result; the server decides who
// does (RelayTeams.OwnerOf), from the house the website sends and the name.

// A relay's name is a count of legs and a leg's distance: 4x100m, 4x60m,
// 4x50m Shuttle. Every relay in the catalogue reads so, and no other event
// does. The server knows a relay by its event code; this is only for what the
// website shows.
export const isRelayEvent = (eventType) =>
  /^\d+\s*x\s*\d+\s*m\b/i.test(String(eventType ?? '').trim());

// The category the server gives a team's result: a house or a club's typed
// team. A school's ad hoc team is a walk-up guest.
export const TEAM_CATEGORY = 'Team';

export const isTeamResult = (result) =>
  result?.athleteCategory === TEAM_CATEGORY;

// The choices for a new team in a school's relay: the houses in list order,
// then "Other team" for an ad hoc team's typed name.
export const OTHER_TEAM = 'other';

export const houseChoices = (houses) =>
  [...(houses || [])]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((house) => ({ value: house.teamID, label: house.name }))
    .concat({ value: OTHER_TEAM, label: 'Other team' });

// The round tabs of a relay's Event View. Every round is followed by an
// Overall tab, which lists the teams of its races by their aggregate position
// (step 3 file, 12.7): the place that counts, even for a round of one race,
// where a school's ad hoc team takes its race place but no overall place
// (Doug, 2026-10-19, at H7 of slice 4). A relay is not in the Year View, so
// this tab is where its placing across races is read. It reads "Overall", or
// "Heat Overall" and "Final Overall" when the relay has both rounds.
// overallOf: a race of the round, to ask for it with. Any other event gets the
// round tabs alone, as before.
export const OVERALL_TAB = 'overall:';

export const roundTabsWithOverall = (siblingRounds, withOverall) => {
  const rounds = siblingRounds || [];
  const racesOf = (roundType) =>
    rounds.filter((round) => round.roundType === roundType);
  const roundTypes = [...new Set(rounds.map((round) => round.roundType))];

  const tabs = [];
  rounds.forEach((round, index) => {
    tabs.push({
      label: `${round.roundType} ${round.roundNumber}`,
      type: round.roundType,
      value: round.eventID,
    });
    const lastOfItsRound = !rounds
      .slice(index + 1)
      .some((later) => later.roundType === round.roundType);
    if (withOverall && lastOfItsRound) {
      tabs.push({
        label:
          roundTypes.length === 1 ? 'Overall' : `${round.roundType} Overall`,
        type: round.roundType,
        value: `${OVERALL_TAB}${round.roundType}`,
        overallOf: racesOf(round.roundType)[0].eventID,
      });
    }
  });
  return tabs;
};

// An Overall tab's rows for its table (api get-relay-overall): the team's
// aggregate position, its name, the race it ran in, its result and status.
export const overallTableRows = (rows) =>
  (rows || []).map((row) => ({
    ...row,
    overallPosition: row.overallPosition ?? '',
    athleteName: row.teamName,
    race: `${row.roundType} ${row.roundNumber}`,
    result: row.status === 'OK' ? row.resultValue : '',
  }));

// ---- A team's runners (step 3 file, section 13) ----
//
// Up to one athlete a leg, typed after the race's results are saved. They
// count for nothing: no place, points or bests. The server checks them again
// (RelayRunners) and keeps a snapshot of each name.

// A relay's legs: the count in its name (4x100m: 4). 0 for any other event.
export const relayLegCount = (eventType) => {
  const match = /^\s*(\d+)\s*x\s*\d+\s*m\b/i.exec(String(eventType ?? ''));
  return match ? Number(match[1]) : 0;
};

// Whether a row of a relay's results dialog has a Runners button: a saved
// team's result, a house's or a club's team. A school's ad hoc team (its
// runners are not the meet's athletes) and a row not saved yet have none.
export const hasRunnersButton = (row) => isTeamResult(row) && !row?.isNew;

// The Runners button's words: "Add runners", or how many there are.
export const runnersButtonLabel = (runners) => {
  const count = (runners || []).length;
  if (count === 0) return 'Add runners';
  return count === 1 ? '1 runner' : `${count} runners`;
};

// The Runners dialog's legs, Leg 1 to the relay's last, each with its runner
// or none.
export const runnerLegs = (runners, legCount) =>
  Array.from({ length: Math.max(legCount, 0) }, (_, index) => {
    const leg = index + 1;
    const runner = (runners || []).find((r) => r.leg === leg);
    return {
      leg,
      athleteID: runner?.athleteID || '',
      name: runner?.name || '',
    };
  });

// The athletes a leg's search offers: the meet's athletes with a number, as
// the results dialog offers them, less those already on another leg of the
// team (an athlete runs one leg of a team).
export const runnerChoicesForLeg = (athletes, legs, leg) => {
  const onOtherLegs = new Set(
    (legs || [])
      .filter((l) => l.leg !== leg && l.athleteID)
      .map((l) => l.athleteID)
  );
  return (athletes || [])
    .filter((a) => a.clubAthleteNumber && !onOtherLegs.has(a.athleteID))
    .map((a) => ({
      athleteID: a.athleteID,
      clubAthleteNumber: a.clubAthleteNumber,
      firstName: a.firstName,
      lastName: a.lastName,
      gender: a.gender,
      ageGroup: a.ageGroup,
    }));
};

// What Save sends: the legs with a runner, in leg order. None clears them.
export const runnersRequest = (legs) =>
  (legs || [])
    .filter((l) => l.athleteID)
    .sort((a, b) => a.leg - b.leg)
    .map(({ leg, athleteID, name }) => ({ leg, athleteID, name }));

// The runners' names under a team's name on the Event View and the Overall
// tab, in leg order.
export const runnerNamesLine = (runners) =>
  [...(runners || [])]
    .sort((a, b) => a.leg - b.leg)
    .map((r) => r.name)
    .join(', ');
